import { Box, Flex, Stack, Text } from "@mantine/core";
import { IconAlertCircle, IconListDetails } from "@tabler/icons-react";
import IconCashRegister from "assets/icons/payroll/cash-register.svg";
import clsx from "clsx";
import { HEADER_HEIGHT } from "common/constants";
import { AppMenuLink, NestedMenu, useAppLinks } from "components/AppMenu";
import LogoutButton from "components/LogoutButton";
import UserAvatar from "components/UserAvatar/UserAvatar";
import { CSSProperties, useEffect } from "react";
import { useTranslation } from "react-i18next";
import packageJson from "src/../../../package.json";
import { useAuthStore } from "stores/authStore";
import LanguageChooser from "../../LanguageChooser";

interface NavigationMenuProps {
  style?: CSSProperties;
  toggleMenuIsOpen: () => void;
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  toggleMenuIsOpen,
  style,
}) => {
  const { t } = useTranslation("common");

  const email = useAuthStore(state => state.email);
  const avatar = useAuthStore(state => state.avatar);
  const fullName = useAuthStore(state => state.fullName);
  const { mainMenuLinks, basicInfoLinks, exceptionLinks, payrollLinks } =
    useAppLinks();

  useEffect(() => {
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  return (
    <Flex
      bg={"white"}
      pos={"fixed"}
      w={"100vw"}
      bottom={0}
      top={HEADER_HEIGHT}
      right={0}
      direction={"column"}
      justify={"space-between"}
      className="z-[200] overflow-y-auto"
      style={style}
    >
      <Text fz={"xs"} c="gray" mr={8} className="self-end">
        JAPP v{packageJson.version}
      </Text>
      <Flex fz={"sm"} lh={"1.45"} direction={"column"} className="grow">
        <Box tt={"uppercase"} className="grow">
          <Stack gap={0} className="text-gray-medium">
            {mainMenuLinks.map(({ link, label, icon, isActive, badge }) => (
              <AppMenuLink
                className={clsx(
                  "flex items-center px-5 py-4 border-b border-gray-lightest",
                  "hover:text-blue",
                  {
                    "text-blue": isActive,
                  }
                )}
                key={label}
                link={link}
                label={label}
                icon={icon}
                badge={badge}
                onClick={toggleMenuIsOpen}
              />
            ))}

            {(basicInfoLinks.length > 0 ||
              exceptionLinks.length > 0 ||
              payrollLinks.length > 0) && (
              <NestedMenu
                className="w-full border-b border-gray-lightest px-5 py-4"
                label={t("basicInformation")}
                icon={<IconListDetails />}
              >
                {basicInfoLinks.map(
                  ({ link, label, icon, isActive, badge }) => (
                    <AppMenuLink
                      className={clsx(
                        "flex items-center pl-10 pr-5 py-3 border-b border-gray-lightest",
                        "hover:text-blue",
                        {
                          "text-blue": isActive,
                        }
                      )}
                      key={label}
                      badge={badge}
                      link={link}
                      label={label}
                      icon={icon}
                      onClick={toggleMenuIsOpen}
                    />
                  )
                )}
                {exceptionLinks.length > 0 && (
                  <NestedMenu
                    className="w-full pl-10 pr-5 py-3 border-b border-gray-lightest"
                    label={t("damages")}
                    icon={<IconAlertCircle />}
                  >
                    {exceptionLinks.map(({ link, label, icon, isActive }) => (
                      <AppMenuLink
                        className={clsx(
                          "flex items-center pl-14 pr-5 py-3 border-b border-gray-lightest",
                          "hover:text-blue",
                          {
                            "text-blue": isActive,
                          }
                        )}
                        key={label}
                        link={link}
                        label={label}
                        icon={icon}
                        onClick={toggleMenuIsOpen}
                      />
                    ))}
                  </NestedMenu>
                )}
                {payrollLinks.length > 0 && (
                  <NestedMenu
                    className="w-full pl-10 pr-5 py-3 border-b border-gray-lightest"
                    label={t("payroll")}
                    icon={<IconCashRegister className="w-6 h-6 shrink-0" />}
                  >
                    {payrollLinks.map(({ link, label, icon, isActive }) => (
                      <AppMenuLink
                        className={clsx(
                          "flex items-center pl-14 pr-5 py-3 border-b border-gray-lightest",
                          "hover:text-blue",
                          {
                            "text-blue": isActive,
                          }
                        )}
                        key={label}
                        link={link}
                        label={label}
                        icon={icon}
                        onClick={toggleMenuIsOpen}
                      />
                    ))}
                  </NestedMenu>
                )}
              </NestedMenu>
            )}
          </Stack>
        </Box>
        <div className="mx-3 my-3">
          <LanguageChooser className="max-w-[200px]" />
        </div>
        <Flex
          ff={"sans-serif"}
          fz={"xs"}
          fw={400}
          justify={"space-between"}
          align={"center"}
          m={"sm"}
        >
          <Flex align={"center"} fz={"sm"} gap={"sm"}>
            <Flex
              justify={"center"}
              align={"center"}
              className="text-white bg-gray-light w-12 h-12 rounded-full"
            >
              <UserAvatar userName={fullName} avatarId={avatar} />
            </Flex>
            <Text fz={12}>{email}</Text>
          </Flex>

          <LogoutButton />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default NavigationMenu;
